// Capture-client factory — selects the REST or PowerSync adapter at
// runtime based on the `VITE_USE_POWERSYNC_CAPTURE_CLIENT` env flag.
//
// Default behaviour: REST adapter. Setting `VITE_USE_POWERSYNC_CAPTURE_CLIENT='true'`
// at build time switches to the Phase E-2 PowerSync-backed adapter,
// which routes `updateItem` through local SQLite and delegates every
// other mutation kind to the REST adapter.
//
// PRODUCTION RUNS POWERSYNC: `VITE_USE_POWERSYNC_CAPTURE_CLIENT=true` is set
// in the prod build (confirmed 2026-06-18 — all capture mutations route
// through the PowerSync adapter + connector in prod). The earlier
// "production stays on REST until Phase F" note was a deferred-rollout
// artefact and is stale; do not rely on it when reasoning about which write
// path prod exercises.
//
// Why a factory instead of conditional imports at the page level:
// each capture page (room, site, building) creates its own client
// via useMemo. Centralising the routing here keeps the page code
// adapter-agnostic and means a future Phase E-3 cutover is a
// one-line env-var change, not a code edit.

import { db } from '../../hooks/powersync/client'

import {
  createCaptureClient,
  type CaptureClientDeps,
  type CaptureClientWithInternals,
} from './capture-client'
import { createPowerSyncCaptureClient } from './capture-client-powersync'

/**
 * True when the build is configured to route through the PowerSync
 * capture adapter. Reads `import.meta.env.VITE_USE_POWERSYNC_CAPTURE_CLIENT`
 * at module-load time so the value is locked at app start — no per-call
 * env reads, no risk of mid-session flapping.
 */
export const POWERSYNC_CAPTURE_CLIENT_ENABLED: boolean =
  import.meta.env.VITE_USE_POWERSYNC_CAPTURE_CLIENT === 'true'

/**
 * Construct the capture client appropriate for the current build.
 * Returns the same `CaptureClientWithInternals` shape regardless of
 * which adapter is selected, so callers don't need to branch.
 */
export function createSelectedCaptureClient(
  deps: CaptureClientDeps = {},
): CaptureClientWithInternals {
  if (POWERSYNC_CAPTURE_CLIENT_ENABLED) {
    return createPowerSyncCaptureClient({ ...deps, db })
  }
  return createCaptureClient(deps)
}
